import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CalendarClock, ChevronDown, ChevronUp, Save } from 'lucide-react';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useDTalkCycleConfig, useSetDTalkCycleConfig } from '@/hooks/usePerformance';
import { useTranslation } from 'react-i18next';

/**
 * Configuración del ciclo de D-Talk (corte de antigüedad, ventana de
 * conversación y label). Los hooks existían sin UI: las fechas quedaban
 * clavadas en los defaults 2026 sin forma de cambiarlas desde la plataforma.
 * Mismo patrón colapsable que CycleConfigPanel del Mapa de Talento.
 */
export function DTalkCycleConfigPanel() {
  const { t } = useTranslation();
  const { data: config, isLoading } = useDTalkCycleConfig();
  const setConfig = useSetDTalkCycleConfig();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    eligibilityCutoff: '',
    windowStart: '',
    windowEnd: '',
    cycleLabel: '',
  });
  // Acceso anticipado: en la UI es texto libre (un email por línea o coma); al guardar se normaliza.
  const [earlyAccess, setEarlyAccess] = useState('');

  useEffect(() => {
    if (config) {
      const { earlyAccessEmails, ...rest } = config;
      setForm(rest);
      setEarlyAccess((earlyAccessEmails ?? []).join('\n'));
    }
  }, [config]);
  const earlyAccessList = earlyAccess.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean);
  const earlyAccessValid = earlyAccessList.every((e) => /^[^@\s]+@winclap\.com$/.test(e));

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  // El corte vacío es válido: vuelve al modo automático (3 meses al momento del intento)
  const canSave = (form.eligibilityCutoff === '' || /^\d{4}-\d{2}-\d{2}$/.test(form.eligibilityCutoff))
    && /^\d{4}-\d{2}-\d{2}$/.test(form.windowStart)
    && /^\d{4}-\d{2}-\d{2}$/.test(form.windowEnd)
    && form.cycleLabel.trim().length > 0
    && earlyAccessValid;

  return (
    <Card>
      <CardHeader className="pb-2 cursor-pointer select-none" onClick={() => setOpen((o) => !o)}>
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-muted-foreground" />
            {t('performance.dtalkcycleconfigpanel.ciclo_d_talk_ventana_y_elegibilidad')}
          </span>
          {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </CardTitle>
      </CardHeader>

      {open && (
        <CardContent className="pt-0 space-y-3">
          {isLoading ? (
            <WinclapLoader size={20} />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground font-medium">{t('performance.dtalkcycleconfigpanel.nombre_del_ciclo')}</label>
                  <Input value={form.cycleLabel} onChange={set('cycleLabel')} className="h-8 text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground font-medium">{t('performance.dtalkcycleconfigpanel.corte_de_antiguedad_opcional')}</label>
                  <div className="flex items-center gap-1.5">
                    <Input type="date" value={form.eligibilityCutoff} onChange={set('eligibilityCutoff')} className="h-8 text-sm" />
                    {form.eligibilityCutoff !== '' && (
                      <Button
                        type="button" variant="ghost" size="sm" className="h-8 px-2 text-xs shrink-0"
                        title={t('performance.dtalkcycleconfigpanel.volver_al_corte_automatico')}
                        onClick={() => setForm((f) => ({ ...f, eligibilityCutoff: '' }))}
                      >
                        ×
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {t('performance.dtalkcycleconfigpanel.vacio_automatico_3_meses_reales_al')}
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground font-medium">{t('performance.dtalkcycleconfigpanel.inicio_de_la_ventana')}</label>
                  <Input type="date" value={form.windowStart} onChange={set('windowStart')} className="h-8 text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground font-medium">{t('performance.dtalkcycleconfigpanel.fin_de_la_ventana')}</label>
                  <Input type="date" value={form.windowEnd} onChange={set('windowEnd')} className="h-8 text-sm" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('performance.dtalkcycleconfigpanel.quien_ingreso_despues_del_corte_queda')}
              </p>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground font-medium">{t('performance.dtalkcycleconfigpanel.acceso_anticipado')}</label>
                <Textarea
                  value={earlyAccess}
                  onChange={(e) => setEarlyAccess(e.target.value)}
                  rows={2}
                  placeholder="nombre.apellido@winclap.com"
                  className={`text-sm ${earlyAccessValid ? '' : 'border-destructive'}`}
                />
                <p className="text-xs text-muted-foreground">{t('performance.dtalkcycleconfigpanel.acceso_anticipado_hint')}</p>
              </div>
              <Button size="sm" className="gap-2" disabled={!canSave || setConfig.isPending} onClick={() => setConfig.mutate({ ...form, earlyAccessEmails: earlyAccessList })}>
                {setConfig.isPending ? <WinclapLoader size={14} /> : <Save className="h-4 w-4" />}
                {t('performance.dtalkcycleconfigpanel.guardar_ciclo')}
              </Button>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}
